// YunEmList.js

import { memo, useRef, useEffect } from 'react'
import './YunEmList.css';
import YunSection from '../YunSection/YunSection';
import YunIcon from '../YunIcon/YunIcon';
import AiEm from './AiEm';
import YunEmSet from '../YunEmSet/YunEmSet'

import { getToken } from '../../../utils/auth';
import { openWindow, forMatTune } from '../../../utils/index';
// import { wafCaptchaManager } from '../../../utils/wafCaptchaManager';

// 模块高度常量
const HEIGHTS = {
    main: 50,
    sub: 50,
    ai: 108,
    emset: 180,
    gap: 8
};

/**
 * 计算各模块位置和容器高度
 * @param {boolean} hasAiEm - 是否显示AiEm模块
 * @param {boolean} hasSubEm - 是否显示SubEm模块
 * @param {boolean} hasEmSet - 是否显示EmSet模块
 * @param {boolean} isAiFirst - AiEm是否排在最前面
 * @param {boolean} isOnlyAiEm - 是否只显示AiEm（curSpk模式）
 * @returns {Object} 各模块top值和容器高度
 */
function calcPositions(hasAiEm, hasSubEm, hasEmSet, isAiFirst, isOnlyAiEm) {
    const { main, sub, ai, emset, gap } = HEIGHTS;
    
    // OnlyAiEm特殊情况：只显示AiEm
    if (isOnlyAiEm) {
        return {
            mainTop: 0,
            subTop: 0,
            aiTop: 0,
            emsetTop: 0,
            containerHeight: ai
        };
    }
    
    let mainTop = 0;
    let subTop = 0;
    let aiTop = 0;
    let emsetTop = 0;
    let containerHeight = main;
    
    if (isAiFirst && hasAiEm) {
        // AiFirst模式：AiEm在顶部
        aiTop = 0;
        mainTop = ai + gap;
        
        let currentHeight = ai + gap + main;
        
        if (hasSubEm) {
            subTop = ai + gap + main + gap;
            currentHeight += gap + sub;
        }
        
        if (hasEmSet) {
            if (hasSubEm) {
                emsetTop = ai + gap + main + gap + sub + gap;
                currentHeight += gap + emset;
            } else {
                emsetTop = ai + gap + main + gap;
                currentHeight += gap + emset;
            }
        }
        
        containerHeight = currentHeight;
    } else {
        // 默认模式：Main在顶部，顺序为 Main -> Sub -> EmSet -> Ai
        mainTop = 0;
        let currentHeight = main;
        let lastBottom = main;
        
        if (hasSubEm) {
            subTop = main + gap;
            lastBottom = subTop + sub;
            currentHeight = lastBottom;
        }
        
        if (hasEmSet) {
            if (hasSubEm) {
                emsetTop = subTop + sub + gap;
            } else {
                emsetTop = main + gap;
            }
            lastBottom = emsetTop + emset;
            currentHeight = lastBottom;
        }
        
        if (hasAiEm) {
            if (hasEmSet) {
                aiTop = emsetTop + emset + gap;
            } else if (hasSubEm) {
                aiTop = subTop + sub + gap;
            } else {
                aiTop = main + gap;
            }
            currentHeight = aiTop + ai;
        }
        
        containerHeight = currentHeight;
    }
    
    return {
        mainTop,
        subTop,
        aiTop,
        emsetTop,
        containerHeight
    };
}

const YunEmList = memo(function(props) {
    const {
        pageset,
        isExpire,
        isBanned,
        emList,
        emPluginState,
        currentKeynote,
        setCurrentKeyNote,
        emSharpFlatState,
        sharpFlatState,
        handleShowEmSet,
        isShowEmSet,
        aiEmData,
        currentAiEm,
        setCurrentAiEm,
        isAiFirst,
        isAutoAiFirst,
        setAiEmFirst,
        setIsCheckedAiEm,
        curSpk,
        clearAiEm,
        emTexts: emTextsProp,
    } = props;

    // 界面文案（后台可配）：没传就用默认
    const emTexts = emTextsProp || {
        subTitle: '其他可尝试的基调',
        subTip: '请选择适合自己的',
        aiEmptyTitle: 'AI分析未采集到音频信号',
        aiEmptyTip: '请选择正在使用的播放器',
    };

    // 用于跟踪各模块上一次的显示状态，实现动画优化
    const prevShowStateRef = useRef({
        main: true,
        sub: false,
        ai: false,
        emset: false
    });

    // // 账号受限：在渲染提交后再通知弹框打开，避免在 render 阶段触发跨组件 setState
    // useEffect(() => {
    //     if (emList && emList.err === 'ERR_USER_RESTRICTED') {
    //         wafCaptchaManager.setNeedRestricted(true);
    //     }
    // }, [emList]);

    /**
     * 获取模块的transition样式
     * @param {boolean} isVisible - 当前是否显示
     * @param {boolean} prevVisible - 上一次是否显示
     * @returns {string} transition样式
     */
    const getTransitionStyle = (isVisible, prevVisible) => {
        if (isVisible && !prevVisible) {
            // 从隐藏变为显示：top无动画，left有动画
            return 'left 0.3s 0.3s';
        } else if (!isVisible && prevVisible) {
            // 从显示变为隐藏：top无动画，left有动画
            return 'top 0s 0.3s, left 0.3s';
        }
        // 其他情况（位置重排）：top和left都有动画
        return 'top 0.3s, left 0.3s';
    };

    // 修改选择的基调
    const clickChangeKeynote = (e, em) => {
        if (e.preventDefault) e.preventDefault()
        if (e.stopPropagation) e.stopPropagation()
        setCurrentKeyNote(em)
        setIsCheckedAiEm(false)
    }
    // 修改选择得AI基调
    const checkedAiEm = (em) => {
        setIsCheckedAiEm(true)
        setCurrentKeyNote(em)
    }

    // 展开自定义基调选择
    const clickExpandEmSet = (e) => {
        if (e.preventDefault) e.preventDefault()
        if (e.stopPropagation) e.stopPropagation()
        handleShowEmSet()
    }

    // 打开原厂充值页（server/empay），商品来自我们后台
    const clickOpenPayWindow = () => {
        const base = window.__EM_API_BASE__ || window.location.origin
        openWindow(base + '/empay/?token=' + getToken())
    }

    // 设置AI优先
    const clickSetAiFrst = () => {
        setAiEmFirst(!isAiFirst)
    }

    // 跳转到帮助
    function clickToHelpPage() {
        openWindow(window.location.origin + window.location.pathname + '#/help');
    }

    let mainEmEle = null
    let subEmEle = null
    // 主基调
    if (emList && emList.err === 'ERR_OK' && Array.isArray(emList.emusics) && emList.emusics[0]) {
        const mainEm = emList.emusics[0]
        const isCurrentEm = mainEm && mainEm.id === currentKeynote.id
        mainEmEle = (
            <YunSection active={isCurrentEm} className={currentKeynote.type} width="200" height="50">
                <div onClick={(e) => clickChangeKeynote(e, mainEm)} className="EmItem">
                    <div className="EmItemL">
                        <div className="dd">
                            <p className="keynote">{mainEm.newKeyNote}</p>
                            <div>
                                <p className="tune MainColor">{forMatTune(emSharpFlatState.value, isCurrentEm)}</p>
                                <p className="tune">{forMatTune(sharpFlatState.value, isCurrentEm)}</p>
                            </div>
                        </div>
                        <p className="scale HoverColor MainColor">{mainEm.newScale}</p>
                    </div>
                    <div className="EmItemR">
                        <p className="musicName" title={mainEm.musicName}>{mainEm.musicName}</p>
                        <p className="musicAuthor HoverColor MainColor" title={mainEm.musicAuthor}>{mainEm.musicAuthor}</p>
                    </div>
                </div>
            </YunSection>
        )
    } else if (emList && emList.err === 'ERR_NO_EM') {
        if (aiEmData) {
            mainEmEle = (
                <YunSection width="200" height="50">
                    <p className="ErrInfo">已交由AI智能基调分析</p>
                </YunSection>
            )
        } else {
            mainEmEle = (
                <YunSection width="200" height="50">
                    <p className="ErrInfo">暂无基调信息，已提交服务器</p>
                    <p className="MainColor HoverColor SubErrInfo">建议您开启AI分析获取基调</p>
                </YunSection>
            )
        }
    } else if (emList && emList.err === 'ERR_EM_TRY_USE_END') {
        mainEmEle = (
            <YunSection width="200" height="50">
                <p className="ErrInfo">会员时间到了，充值支持一下吧</p>
                <div className="PayBtn YunBtn" onClick={clickOpenPayWindow}>充值</div>
            </YunSection>
        )
    } else if (emList && emList.err === 'ERR_EM_FREQUENTLY') {
        mainEmEle = (
            <YunSection width="200" height="50">
                <p className="ErrInfo">您切歌太快了~我都跟不上啦！</p>
                <p className="MainColor HoverColor SubErrInfo">请稍微休息一下吧</p>
            </YunSection>
        )
    } else if (emList && emList.err === 'EMPTY') {
        mainEmEle = (
            <YunSection width="200" height="50">
                <p className="ErrInfo">没有发现基调信息</p>
            </YunSection>
        )
    } else if (emList && emList.err === 'NETWORK_ERR') {
        mainEmEle = (
            <YunSection width="200" height="50">
                <p className="ErrInfo">网络错误</p>
            </YunSection>
        )
    } else if (emList && emList.err === 'NETWORK_ERR_D') {
        mainEmEle = (
            <YunSection width="200" height="50">
                <p className="ErrInfo">服务器被攻击，正在全力抢修，请稍后再试。</p>
            </YunSection>
        )
    }
    // else if (emList && emList.err === 'ERR_USER_RESTRICTED') {
    //     mainEmEle = (
    //         <YunSection width="200" height="50">
    //             <p className="ErrInfo">账号异常，请用微信扫码认证</p>
    //         </YunSection>
    //     )
    // }
    else {
        mainEmEle = (
            <YunSection width="200" height="50">
                <p className="ErrInfo">请播放歌曲吧！</p>
            </YunSection>
        )
    }

    // 插件连接的时候才显示第二基调
     if (Array.isArray(emPluginState) && emPluginState.length) {
        if (Array.isArray(emList.emusics) && emList.emusics.length > 1) {
            const mainEm = emList.emusics[0]
            const subEm = emList.emusics[1]
            const isCurrentEm = subEm.id === currentKeynote.id
            // 如果是歌手和作者和第一基调相同 则显示不同样式（其他用户试的基调）
            if (subEm.musicName === mainEm.musicName && subEm.musicAuthor === mainEm.musicAuthor) {
                subEmEle = (
                    <YunSection className={currentKeynote.type + ' SubEmSection'} active={isCurrentEm} width="200" height="50">
                        <div onClick={(e) => clickChangeKeynote(e, subEm)} className="EmItem">
                            <div className="EmItemL">
                                <div className="dd">
                                    <p className="keynote">{subEm.newKeyNote}</p>
                                    <div>
                                        <p className="tune MainColor">{forMatTune(emSharpFlatState.value, isCurrentEm)}</p>
                                        <p className="tune">{forMatTune(sharpFlatState.value, isCurrentEm)}</p>
                                    </div>
                                </div>
                                <p className="scale HoverColor MainColor">{subEm.newScale}</p>
                            </div>
                            <div className="EmItemR">
                                <p className="musicName">{emTexts.subTitle}</p>
                                <p className="musicAuthor HoverColor MainColor">{emTexts.subTip}</p>
                            </div>
                        </div>
                        <div className={isShowEmSet ? "ExpandBtn YunBtnColor Active" : "ExpandBtn YunBtnColor"} onClick={(e) => clickExpandEmSet(e)}>
                            <YunIcon icon="arrowDown"></YunIcon>
                        </div>
                    </YunSection>
                )
            } else {
                subEmEle = (
                    <YunSection className={currentKeynote.type + ' SubEmSection'} active={isCurrentEm} width="200" height="50">
                        <div onClick={(e) => clickChangeKeynote(e, subEm)} className="EmItem">
                            <div className="EmItemL">
                                <div className="dd">
                                    <p className="keynote">{subEm.newKeyNote}</p>
                                    <div>
                                        <p className="tune MainColor">{forMatTune(emSharpFlatState.value, isCurrentEm)}</p>
                                        <p className="tune">{forMatTune(sharpFlatState.value, isCurrentEm)}</p>
                                    </div>
                                </div>
                                <p className="scale HoverColor MainColor">{subEm.newScale}</p>
                            </div>
                            <div className="EmItemR">
                                <p className="musicName" title={subEm.musicName}>{subEm.musicName}</p>
                                <p className="musicAuthor HoverColor MainColor" title={subEm.musicAuthor}>{subEm.musicAuthor}</p>
                            </div>
                        </div>
                        <div className={isShowEmSet ? "ExpandBtn YunBtnColor Active" : "ExpandBtn YunBtnColor"} onClick={(e) => clickExpandEmSet(e)}>
                            <YunIcon icon="arrowDown"></YunIcon>
                        </div>
                    </YunSection>
                )
            }
        } else {
            subEmEle = (
                <YunSection className="SubEmSection" width="200" height="50">
                    <div className="OtherEmTips">可以选择其他基调</div>
                    <div className="MainColor HoverColor">将自动保持您的选择</div>
                    <div className={isShowEmSet ? "ExpandBtn YunBtnColor Active" : "ExpandBtn YunBtnColor"} onClick={(e) => clickExpandEmSet(e)}>
                        <YunIcon icon="arrowDown"></YunIcon>
                    </div>
                </YunSection>
            )
        }
    } else {
        subEmEle = (
            <YunSection className="SubEmSection" width="200" height="50">
                <div className="PluginState">
                    <div className="EmItemL">
                        <p className="MainColor3">
                            电音插件
                        </p>
                        <p onClick={clickToHelpPage} className="PluginStateBtn UnderlineAnimation HoverColor MainColor">
                            未连接
                        </p>
                    </div>
                    <div className="EmItemR">
                        <p>插件不在使用状态</p>
                        <p>或被安全软件阻止</p>
                    </div>
                </div>
                <div className={isShowEmSet ? "ExpandBtn YunBtnColor Active" : "ExpandBtn YunBtnColor"} onClick={(e) => clickExpandEmSet(e)}>
                    <YunIcon icon="arrowDown"></YunIcon>
                </div>
            </YunSection>
        )
    }

    const isAiEm = currentKeynote.type === "EM_KN_AI";
    const lockClass = isAiFirst ? "lock" : "unlock";
    const btnClass = isAiFirst ? "AiEmFirstBtn IconBtn MainColor5" : "IconBtn AiEmFirstBtn";
    let currentAiEmEle = null;
    if (isBanned) {
        // 封禁用户：AI 分析这块直接说清楚，不要再显示「VIP用户专属」引导他充值
        currentAiEmEle = (
            <div className="AiEmInfo Vip">
                <div>
                    <p>账号已被封禁</p>
                    <p className="MainColor HoverColor">所有功能已停用，请联系客服解封</p>
                </div>
            </div>
        )
    } else if (isExpire) {
        currentAiEmEle = (
            <div className="AiEmInfo Vip" onClick={clickOpenPayWindow}>
                <div>
                    <p>AI基调分析为VIP用户专属</p>
                    <p className="MainColor HoverColor">点击成为尊贵VIP会员</p>
                </div>
            </div>
        )
    } else if (currentAiEm && currentAiEm.newKeyNote && currentAiEm.newScale) {
        currentAiEmEle = (
            <div className="CurrentAiEmEle" onClick={() => checkedAiEm(currentAiEm)}>
                <p className="keynote">{currentAiEm.newKeyNote}</p>
                <p className="scale HoverColor MainColor">{currentAiEm.newScale}</p>
            </div>
        )
    } else {
        currentAiEmEle = (
            <div className="AiEmInfo NoAi">
                <div>
                    <p>{emTexts.aiEmptyTitle}</p>
                    <p className="MainColor HoverColor">{emTexts.aiEmptyTip}</p>
                </div>
            </div>
        )
    }
    const aiEmEle = (
        <YunSection key="aiemKey" active={isAiEm} className="AiEm EM_KN_AI" width="200" height="108">
            {currentAiEmEle}
            <div className={btnClass} onClick={clickSetAiFrst}>
                <YunIcon icon={lockClass}></YunIcon>
            </div>
            <div className="ResetBtn IconBtn" onClick={clearAiEm}>
                <YunIcon icon="reset"></YunIcon>
            </div>
            <AiEm data={aiEmData} setCurrentAiEm={setCurrentAiEm} checkedAiEm={checkedAiEm} isAiEm={isAiEm}></AiEm>
        </YunSection>
    )

    // 计算显示状态
    const isOnlyAiEm = !!curSpk;
    const hasAiEm = !!pageset.aiEm;
    const hasSubEm = !!pageset.emBackup;
    const hasEmSet = !!isShowEmSet;
    const showAiFirst = !!(isAiFirst || isAutoAiFirst);

    // 当前各模块的显示状态
    const currentShowState = {
        main: !isOnlyAiEm, // MainEm 在非 OnlyAiEm 模式下显示
        sub: hasSubEm && !isOnlyAiEm,
        ai: hasAiEm || isOnlyAiEm, // OnlyAiEm 模式下 AiEm 显示
        emset: hasEmSet && !isOnlyAiEm
    };

    // 获取上一次的显示状态
    const prevShowState = prevShowStateRef.current;

    // 计算各模块的transition样式
    const mainTransition = getTransitionStyle(currentShowState.main, prevShowState.main);
    const subTransition = getTransitionStyle(currentShowState.sub, prevShowState.sub);
    const aiTransition = getTransitionStyle(currentShowState.ai, prevShowState.ai);
    const emsetTransition = getTransitionStyle(currentShowState.emset, prevShowState.emset);

    // 更新ref
    useEffect(() => {
        prevShowStateRef.current = currentShowState;
    });

    let YunEmListClassName = "YunEmList EmCommon"
    if (isOnlyAiEm) {
        YunEmListClassName += " OnlyAiEm"
    } else {
        if (hasAiEm) {
            YunEmListClassName += " AiEm"
        }
        if (hasSubEm) {
            YunEmListClassName += " SubEm"
        }
        if (hasEmSet) {
            YunEmListClassName += " EmSet"
        }
        if (showAiFirst) {
            YunEmListClassName += " AiFirst"
        }
    }

    // 计算各模块位置和容器高度
    const positions = calcPositions(hasAiEm, hasSubEm, hasEmSet, showAiFirst, isOnlyAiEm);

    return (
        <div className={YunEmListClassName} style={{ height: positions.containerHeight }}>
            <div className="YunEmListItem MainEmWrapper" style={{ top: positions.mainTop, transition: mainTransition }}>
                {mainEmEle}
            </div>
            <div className="YunEmListItem SubEmWrapper" style={{ top: positions.subTop, transition: subTransition }}>
                {subEmEle}
            </div>
            <div className="YunEmListItem AiEmWrapper" style={{ top: positions.aiTop, transition: aiTransition }}>
                {aiEmEle}
            </div>
            <div className="YunEmListItem EmSetWrapper" style={{ top: positions.emsetTop, transition: emsetTransition }}>
                <YunEmSet
                    currentKeynote={currentKeynote}
                    setCurrentKeyNote={setCurrentKeyNote}
                    sharpFlatState={sharpFlatState}
                    emSharpFlatState={emSharpFlatState}
                    setIsCheckedAiEm={setIsCheckedAiEm} >
                </YunEmSet>
            </div>
        </div>
    )
})

export default YunEmList;
